import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { setTutorialMode } from '../services/TutorialInterceptor';
import { fetchWithTrace } from '../services/fetchWithTrace';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const OnboardingContext = createContext();

export const OnboardingProvider = ({ children }) => {
    // Initialize state properly by checking localStorage
    const [isActive, setIsActive] = useState(() => {
        return localStorage.getItem('has_completed_tutorial') === 'false';
    });
    const [currentStep, setCurrentStep] = useState(0);

    // Initial setup on mount
    useEffect(() => {
        if (isActive) {
            setTutorialMode(true);
        }
    }, [isActive]);

    const startTour = useCallback(() => {
        setIsActive(true);
        setCurrentStep(0);
        setTutorialMode(true);

        // Mark complete immediately — if the user refreshes, logs out, or
        // closes the tab mid-tour they will NOT see the tour again.
        localStorage.setItem('has_completed_tutorial', 'true');
        fetchWithTrace(`${API_URL}/tutorial-complete`, { method: 'PATCH', credentials: 'include' })
            .catch(e => console.warn('[Tour] Failed to mark tutorial complete on start', e));
    }, []);

    const nextStep = useCallback(() => {
        setCurrentStep((prev) => prev + 1);
    }, []);

    const skipTour = useCallback(() => {
        setIsActive(false);
        setCurrentStep(0);
        setTutorialMode(false);
        localStorage.setItem('has_completed_tutorial', 'true');

        fetchWithTrace(`${API_URL}/tutorial-complete`, { method: 'PATCH', credentials: 'include' })
            .catch(e => console.warn("Failed to mark tutorial complete", e));
    }, []);

    const setStep = useCallback((stepIndex) => {
        setCurrentStep(stepIndex);
    }, []);

    const markStepComplete = useCallback(() => {
        setCurrentStep((prev) => prev + 1);
    }, []);

    useEffect(() => {
        if (!isActive) return;
        const handleEvent = (e) => {
            setCurrentStep((prev) => {
                const next = prev + 1;
                if (next >= 10) { // final step
                    setTutorialMode(false);
                    localStorage.setItem('has_completed_tutorial', 'true');
                    setIsActive(false);

                    fetchWithTrace(`${API_URL}/tutorial-complete`, { method: 'PATCH', credentials: 'include' })
                        .catch(e => console.warn("Failed to mark tutorial complete", e));

                    return 0; // reset
                }
                return next;
            });
        };
        window.addEventListener('tutorial-step-complete', handleEvent);
        return () => window.removeEventListener('tutorial-step-complete', handleEvent);
    }, [isActive]);

    const contextValue = React.useMemo(() => ({
        isActive,
        currentStep,
        startTour,
        skipTour,
        nextStep,
        setStep,
        markStepComplete
    }), [isActive, currentStep, startTour, skipTour, nextStep, setStep, markStepComplete]);

    return (
        <OnboardingContext.Provider value={contextValue}>
            {children}
        </OnboardingContext.Provider>
    );
};

export const useOnboarding = () => useContext(OnboardingContext);
